/**
 * Application shell (E10-F01-T03): navy collapsible sidebar + sticky white
 * header + gray-50 content area with switchable `#view-*` containers.
 *
 * View switching contract (parity with legacy `core.js`):
 *   - one `#view-<id>` container per top-level view; on navigation EVERY
 *     `#view-*` receives `hidden` and only the target clears it;
 *   - the target replays the `.fade-in` entrance animation (re-keyed on each
 *     route change so the animation restarts even when re-selecting a view).
 *
 * The header `page-title` is Playfair (`.brand-font`) and reflects the active
 * route; navigation goes through `useRouter` (history.pushState + route.save +
 * document.title), and browser Back/Forward restores the encoded view.
 *
 * The view bodies themselves are placeholders here — real feature views
 * (Projects dashboard, Inbox, Admin/Users, Project detail) land in later tasks.
 * This task owns only the shell + routing skeleton.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AuthIdentity } from '@eer/shared';
import type { RouterApi } from '../router/useRouter.js';
import { titleForView, VIEW_IDS, viewElementId, type ViewId } from '../router/routes.js';
import { ClientsView } from '../contacts/ClientsView.js';
import { loadSidebarCollapsed, saveSidebarCollapsed } from '../router/storage.js';
import { Sidebar } from './Sidebar.js';
import { useInboxBadgeCount } from './useInboxBadge.js';
import { InboxView } from '../inbox/InboxView.js';
import { DashboardView } from '../dashboard/DashboardView.js';
import { ProjectDetailView } from '../project/ProjectDetailView.js';
import { SettingsView } from '../admin/SettingsView.js';
import { ResearchSection } from '../research/ResearchSection.js';
import { HelpView } from '../help/HelpView.js';
import { HelpAdminView } from '../help/HelpAdminView.js';
import { HelpResearchView } from '../help/HelpResearchView.js';
import { SectionErrorBoundary, useUnsavedGuard } from '../ui/index.js';

export interface AppShellProps {
  /** Authenticated principal (drives the user block + admin nav gating). */
  readonly user: AuthIdentity | null;
  /**
   * The shared router instance (owned by `App` so session-restore navigation and
   * the shell render from the same route state). E10-F01-T05.
   */
  readonly router: RouterApi;
  /** Sign out — best-effort server logout + local clear (flips to login). */
  readonly onLogout?: () => void;
}

/** Placeholder body for a not-yet-implemented view container. */
function ViewPlaceholder({ view }: { view: ViewId }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface p-8 text-text-muted">
      <p className="text-sm">
        <span className="brand-font text-text-strong">{titleForView(view)}</span> view — coming in a
        later task.
      </p>
    </div>
  );
}

/**
 * Render the body for an active view. `projects` → the E02-F01 Dashboard (stat
 * cards / table / filters / New Project), `project` → the E02-F02 detail card,
 * `inbox` → the Inbox; `users` → the Admin/Settings tabbed view (E05 wire-pass).
 * The dashboard/inbox open a project's card by navigating to `/project/:id`.
 */
function renderView(
  view: ViewId,
  routeId: string | null,
  navigate: (route: { view: ViewId; id?: string }) => void,
  user: AppShellProps['user'],
  researchNavNonce: number,
  onSettingsTabChange: (label: string) => void,
): React.JSX.Element {
  const openProject = (id: number): void => navigate({ view: 'project', id: String(id) });
  switch (view) {
    case 'projects':
      // Thread the authenticated principal's id so the Dashboard's "My Active"
      // scope pill (E02-F01-T08) can match `owner_id === currentUser.id`.
      return <DashboardView onOpen={openProject} currentUserId={user?.id ?? null} />;
    case 'project': {
      const projectId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
      return (
        <ProjectDetailView projectId={projectId} onClose={() => navigate({ view: 'projects' })} />
      );
    }
    case 'inbox': {
      const emailId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
      return <InboxView initialSelectedEmailId={emailId} />;
    }
    case 'research':
      // BUG-9: the `key` is bumped by AppShell on a re-click of the already-active
      // Research nav; the changed key force-remounts ResearchSection, resetting its
      // internal `screen` state back to the library/history default (the router's
      // same-view no-op means navigate() alone cannot reset a Research sub-screen).
      return <ResearchSection key={researchNavNonce} />;
    case 'users':
      return (
        <SettingsView
          currentUserId={user?.id ?? null}
          currentUserRole={user?.role ?? null}
          onActiveTabChange={onSettingsTabChange}
        />
      );
    case 'help':
      // E13-F01 — the in-app user guide. Pure static content (no queries, no
      // API), but it is still wrapped in the shared boundary like the Settings
      // panels: a crash inside one guide section must degrade to an inline
      // message, not white-screen the shell the user came here for help with.
      return (
        <SectionErrorBoundary label="Help">
          <HelpView user={user} onOpenGuide={(page) => navigate({ view: page })} />
        </SectionErrorBoundary>
      );
    case 'help_admin':
      // E13-F02 — the admin guide, reached from the "Admin guide" card on
      // `/help` (role-gated there). Same error-boundary treatment as `help`.
      return (
        <SectionErrorBoundary label="Help">
          <HelpAdminView onBack={() => navigate({ view: 'help' })} />
        </SectionErrorBoundary>
      );
    case 'help_research':
      // E13-F03 — the Research guide, reached from the "Research guide" card
      // on `/help`. Same error-boundary treatment as `help`.
      return (
        <SectionErrorBoundary label="Help">
          <HelpResearchView onBack={() => navigate({ view: 'help' })} />
        </SectionErrorBoundary>
      );
    case 'clients': {
      // KS-02 — the selected client lives in the URL so a row under discussion can be
      // linked to. `null` (no id) is a real state: the list with nothing selected.
      const clientId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
      return (
        <ClientsView
          selectedId={clientId}
          onSelect={(id) =>
            navigate(id === null ? { view: 'clients' } : { view: 'clients', id: String(id) })
          }
        />
      );
    }
    default:
      return <ViewPlaceholder view={view} />;
  }
}

export function AppShell({ user, router, onLogout }: AppShellProps): React.JSX.Element {
  const { route, navigate } = router;

  // Restore the collapsed preference on mount. AppShell only mounts once the
  // session is authenticated (App renders the shell after `useAuth` resolves to
  // `authenticated`), so this lazy initializer is the React equivalent of the
  // legacy `setCurrentUser → restoreSidebar`: the persisted flag is read before
  // the first content paint, so a collapsed sidebar never "jumps" back to
  // expanded after a reload/relogin.
  const [collapsed, setCollapsed] = useState<boolean>(() => loadSidebarCollapsed());

  // BUG-9: a monotonic nonce bumped whenever the user re-clicks the already-active
  // "Research" nav. Threaded into `<ResearchSection key=…>` so the re-click
  // force-remounts the section back to its default sub-screen (the router no-ops a
  // same-view navigate, so this remount is the only reset lever).
  const [researchNavNonce, setResearchNavNonce] = useState(0);

  // E06-F04-T12 — the active Settings tab label, reported by SettingsView (it
  // remounts on every visit and re-reports its default tab), so the header reads
  // "Settings — <tab>" instead of a fixed "Users".
  const [settingsTab, setSettingsTab] = useState<string | null>(null);
  const pageTitle =
    route.view === 'users' && settingsTab
      ? `${titleForView('users')} — ${settingsTab}`
      : titleForView(route.view);

  // Persist the collapse preference whenever it changes (the legacy
  // `toggleSidebar` wrote `eer.sidebarCollapsed` = `'1' | ''`).
  useEffect(() => {
    saveSidebarCollapsed(collapsed);
  }, [collapsed]);

  // E02-F03-T08 — the unsaved-changes gate. `App` wraps the shell in
  // `UnsavedGuardProvider`, so EVERY navigation the shell performs (sidebar items
  // below + the `navigate` handed to the view bodies) runs through `guard(...)`:
  // with a dirty editor registered (project header edits, Add-Information draft)
  // the Save/Discard/Cancel dialog opens and the navigation is deferred behind the
  // chosen action; with nothing dirty `guard` invokes the callback synchronously,
  // so this wrapper is invisible in the common case. Before this, `guard` was
  // never called outside its own unit tests and the dialog could not appear in the
  // live app at all.
  const { guard } = useUnsavedGuard();
  const guardedNavigate = useCallback(
    (next: { view: ViewId; id?: string }): void => {
      guard(() => navigate(next));
    },
    [guard, navigate],
  );

  const toggleSidebar = (): void => setCollapsed((c) => !c);
  const goTo = (view: ViewId): void => {
    guard(() => {
      // BUG-9: re-clicking Research while already inside a Research sub-screen is a
      // router no-op (same view+id) — bump the remount nonce so ResearchSection
      // resets to its default sub-screen instead of dead-ending. Inside the guard
      // callback so a cancelled navigation does not reset the section either.
      if (view === 'research' && route.view === 'research') {
        setResearchNavNonce((n) => n + 1);
      }
      navigate({ view });
    });
  };

  // Inbox unread badge — the legacy `#inbox-badge` count. The sidebar `badges`
  // prop existed but was never fed; here we source it from the actionable
  // (`new_project_candidate`) inbox subset (see `useInboxBadge`). `0` hides it.
  const inboxUnread = useInboxBadgeCount(Boolean(user));

  return (
    // E10-F01-T08 — the shell is a fixed-height viewport frame (`h-screen
    // overflow-hidden`), NOT a `min-h-screen` page that grows with content. This
    // moves the scroll OUT of the window and INTO `<main>` (below), so the navy
    // `<aside h-screen>` and the sticky header stay pinned while only the content
    // column scrolls — no more window-level scroll that slid the sidebar up and
    // left a white gap below its slate-900 background.
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        collapsed={collapsed}
        route={route}
        user={user}
        onToggle={toggleSidebar}
        onNavigate={goTo}
        onLogout={onLogout}
        badges={{ inbox: inboxUnread }}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Sticky white header. The logout control lives at the BOTTOM of the
            sidebar (Sidebar.tsx, FP-15) — the PM reverted the brief move of
            "Sign out" up into this header. */}
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-border bg-white px-8">
          <h1
            id="page-title"
            data-testid="page-title"
            className="brand-font text-2xl font-bold text-text-strong"
          >
            {pageTitle}
          </h1>
        </header>

        {/* Content area — switchable view containers. This is the ONLY scroll
            container (E10-F01-T08): `flex-1` claims the space under the fixed
            header, `min-h-0` lets the flex item shrink below its content (the
            classic flexbox-scroll gotcha — without it the item grows to content
            height and the scroll escapes to the window), and `overflow-y-auto`
            keeps the sidebar + header pinned while the body scrolls. */}
        <main className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-8">
          {VIEW_IDS.map((view) => {
            const active = view === route.view;
            return (
              <section
                key={view}
                id={viewElementId(view)}
                data-testid={viewElementId(view)}
                hidden={!active}
                // Re-key on each route change so `.fade-in` restarts on the
                // active view even when re-selected.
                className={active ? 'fade-in' : undefined}
              >
                {active
                  ? renderView(
                      view,
                      route.id ?? null,
                      guardedNavigate,
                      user,
                      researchNavNonce,
                      setSettingsTab,
                    )
                  : null}
              </section>
            );
          })}
        </main>
      </div>
    </div>
  );
}
